import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Edit, 
  Check, 
  Target, 
  Brain, 
  Palette, 
  TrendingUp, 
  FileText, 
  Link, 
  Plus, 
  Calendar,
  Clock,
  Lightbulb,
  BookOpen,
  CheckCircle2,
  Circle,
  MoreHorizontal,
  Download,
  Share
} from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

export default function ActiveProjectWorkspace({ projectConfig, onClose, onSave }) {
  const { currentUser } = useFamilyContext();
  const [currentPhase, setCurrentPhase] = useState(0);
  const [projectVision, setProjectVision] = useState('');
  const [documentation, setDocumentation] = useState('');
  const [resources, setResources] = useState([]);
  const [insights, setInsights] = useState([]);
  const [phaseProgress, setPhaseProgress] = useState({});
  const [newInsight, setNewInsight] = useState('');
  const [newResource, setNewResource] = useState({ title: '', url: '', type: 'link' });
  const [showAddResource, setShowAddResource] = useState(false);

  useEffect(() => {
    if (projectConfig) {
      // Initialize project vision from the original idea
      setProjectVision(projectConfig.originalIdea?.core_problem || projectConfig.originalIdea?.coreProblem || '');
      
      // Initialize resources from the framework
      const initialResources = projectConfig.originalIdea?.resource_clusters || projectConfig.originalIdea?.resourceClusters || [];
      setResources(initialResources);
      
      // Initialize phase progress
      const initialProgress = {};
      projectConfig.customization?.plan?.phases?.forEach((phase, index) => {
        initialProgress[phase.id] = {
          status: index === 0 ? 'in_progress' : 'pending',
          completed_activities: [],
          notes: ''
        };
      });
      setPhaseProgress(initialProgress);
    }
  }, [projectConfig]);

  const getPathwayIcon = (pathway) => {
    const icons = {
      research: Brain,
      technical: Target, 
      design: Palette,
      business: TrendingUp
    };
    return icons[pathway] || Target;
  };

  const addInsight = () => {
    if (newInsight.trim()) {
      const insight = {
        id: Date.now(),
        content: newInsight,
        timestamp: new Date().toISOString(),
        phase: currentPhase + 1,
        author: currentUser?.name || 'You'
      };
      setInsights(prev => [insight, ...prev]);
      setNewInsight('');
    }
  };

  const addResource = () => {
    if (newResource.title.trim() && newResource.url.trim()) {
      const resource = {
        id: Date.now(),
        ...newResource,
        added_at: new Date().toISOString()
      };
      setResources(prev => [...prev, resource]);
      setNewResource({ title: '', url: '', type: 'link' });
      setShowAddResource(false);
    }
  };

  const updatePhaseProgress = (phaseId, updates) => {
    setPhaseProgress(prev => ({
      ...prev,
      [phaseId]: {
        ...prev[phaseId],
        ...updates
      }
    }));
  };

  const toggleActivityComplete = (phaseId, activity) => {
    const currentProgress = phaseProgress[phaseId] || { completed_activities: [] };
    const completedActivities = currentProgress.completed_activities || [];
    
    const updatedActivities = completedActivities.includes(activity)
      ? completedActivities.filter(a => a !== activity)
      : [...completedActivities, activity];
    
    updatePhaseProgress(phaseId, { completed_activities: updatedActivities });
  };

  const getCurrentPhaseData = () => {
    return projectConfig?.customization?.plan?.phases?.[currentPhase];
  };

  const getPhaseCompletionPercentage = (phase) => {
    const progress = phaseProgress[phase.id];
    if (!progress || !phase.keyActivities) return 0;
    
    const completedCount = progress.completed_activities?.length || 0;
    return Math.round((completedCount / phase.keyActivities.length) * 100);
  };

  const handleSaveProject = () => {
    const projectData = {
      id: Date.now(),
      originalIdea: projectConfig.originalIdea,
      customization: projectConfig.customization,
      workspace: {
        projectVision,
        documentation,
        resources,
        insights,
        phaseProgress,
        currentPhase,
        lastUpdated: new Date().toISOString()
      },
      userId: currentUser?.id,
      createdAt: new Date().toISOString()
    };
    
    onSave?.(projectData);
  };

  if (!projectConfig) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-stone-900 mb-2">No Project Configuration</h2>
          <p className="text-stone-600">Please start a project through the setup flow.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-stone-50 rounded-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 rounded-t-2xl flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={onClose}
                className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{projectConfig.originalIdea?.title}</h1>
                <p className="text-sm text-stone-600">
                  Active Project • {projectConfig.customization?.pathways?.join(' + ') || 'Multi-track'} Approach
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveProject}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Save Progress
              </button>
              <button className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">
                <Share className="w-5 h-5" />
              </button>
              <button className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
              </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Vision */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Project Vision</h2>
                <button className="p-1 text-stone-400 hover:text-stone-600">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={projectVision}
                onChange={(e) => setProjectVision(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="Refine your project vision based on what you've learned..."
              />
              {projectConfig.customization?.interest && (
                <div className="mt-3 text-sm text-stone-600">
                  <strong>Your Focus:</strong> {projectConfig.customization.interest}
                </div>
              )}
            </div>

            {/* Phase Navigation */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Project Phases</h2>
              <div className="flex flex-wrap gap-2 mb-6">
                {projectConfig.customization?.plan?.phases?.map((phase, index) => {
                  const completion = getPhaseCompletionPercentage(phase);
                  const isActive = currentPhase === index;
                  return (
                    <button
                      key={phase.id}
                      onClick={() => setCurrentPhase(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-teal-600 text-white'
                          : completion === 100
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : completion > 0
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {completion === 100 ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : completion > 0 ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                        Phase {phase.id}: {phase.title}
                        {completion > 0 && (
                          <span className="text-xs">({completion}%)</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Current Phase Details */}
              {getCurrentPhaseData() && (
                <div className="border-t border-stone-200 pt-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Target className="w-6 h-6 text-teal-600 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Phase {getCurrentPhaseData().id}: {getCurrentPhaseData().title}
                      </h3>
                      <p className="text-sm text-stone-600 mt-1">
                        {getCurrentPhaseData().description}
                      </p>
                      <span className="inline-block mt-2 px-2 py-1 bg-stone-100 text-stone-700 rounded text-xs font-medium">
                        {getCurrentPhaseData().intensity}
                      </span>
                    </div>
                  </div>

                  {/* Key Activities Checklist */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-stone-900">Key Activities</h4>
                    {getCurrentPhaseData().keyActivities?.map((activity, index) => {
                      const isCompleted = phaseProgress[getCurrentPhaseData().id]?.completed_activities?.includes(activity);
                      return (
                        <div
                          key={index}
                          onClick={() => toggleActivityComplete(getCurrentPhaseData().id, activity)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isCompleted
                              ? 'border-emerald-200 bg-emerald-50'
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isCompleted
                              ? 'border-emerald-500 bg-emerald-500 text-white'
                              : 'border-stone-300'
                          }`}>
                            {isCompleted && <Check className="w-3 h-3" />}
                          </div>
                          <span className={`text-sm ${
                            isCompleted ? 'text-emerald-800 line-through' : 'text-stone-700'
                          }`}>
                            {activity}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Phase Notes */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-stone-700 mb-2">Phase Notes</label>
                    <textarea
                      value={phaseProgress[getCurrentPhaseData().id]?.notes || ''}
                      onChange={(e) => updatePhaseProgress(getCurrentPhaseData().id, { notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Add notes about your progress, challenges, or insights for this phase..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Documentation Space */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Documentation</h2>
                <FileText className="w-5 h-5 text-stone-400" />
              </div>
              <textarea
                value={documentation}
                onChange={(e) => setDocumentation(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Document your research, decisions, iterations, and learnings here..."
              />
              <div className="mt-2 text-xs text-stone-500">
                Tip: Include research findings, design decisions, test results, and reflections on your process
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Chosen Pathways */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Your Approach</h3>
              <div className="space-y-3">
                {projectConfig.customization?.pathways?.map(pathway => {
                  const IconComponent = getPathwayIcon(pathway);
                  return (
                    <div key={pathway} className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                      <div className="w-8 h-8 bg-teal-600 text-white rounded-lg flex items-center justify-center">
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-900 capitalize">{pathway} Track</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resources */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Resources</h3>
                <button
                  onClick={() => setShowAddResource(!showAddResource)}
                  className="p-1 text-stone-400 hover:text-stone-600"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {showAddResource && (
                <div className="mb-4 p-3 bg-stone-50 rounded-lg space-y-2">
                  <input
                    type="text"
                    value={newResource.title}
                    onChange={(e) => setNewResource(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Resource title"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <input
                    type="url"
                    value={newResource.url}
                    onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={addResource}
                      className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowAddResource(false)}
                      className="px-3 py-1 text-stone-600 hover:bg-stone-100 rounded text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {resources.map((resource, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                    <Link className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-teal-600 hover:text-teal-700 block truncate"
                      >
                        {resource.title}
                      </a>
                      {resource.added_at && (
                        <div className="text-xs text-stone-500 mt-1">
                          Added {new Date(resource.added_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Insights & Reflections */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Insights & Reflections</h3>
              
              <div className="mb-4">
                <textarea
                  value={newInsight}
                  onChange={(e) => setNewInsight(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Capture an insight, lesson learned, or reflection..."
                />
                <button
                  onClick={addInsight}
                  disabled={!newInsight.trim()}
                  className="mt-2 px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lightbulb className="w-3 h-3 inline mr-1" />
                  Add Insight
                </button>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {insights.map((insight) => (
                  <div key={insight.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">{insight.content}</p>
                    <div className="text-xs text-amber-600 mt-2 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      Phase {insight.phase} • {new Date(insight.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                {insights.length === 0 && (
                  <p className="text-sm text-stone-500 text-center py-4">
                    No insights captured yet. Start documenting your discoveries!
                  </p>
                )}
              </div>
            </div>

            {/* Progress Summary */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Progress Summary</h3>
              <div className="space-y-3">
                {projectConfig.customization?.plan?.phases?.map((phase) => {
                  const completion = getPhaseCompletionPercentage(phase);
                  return (
                    <div key={phase.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-stone-700">Phase {phase.id}</span>
                        <span className="text-stone-500">{completion}%</span>
                      </div>
                      <div className="w-full bg-stone-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            completion === 100 ? 'bg-emerald-500' : 'bg-teal-500'
                          }`}
                          style={{ width: `${completion}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}